import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  DocumentArrowDownIcon, 
  ShieldCheckIcon, 
  UserGroupIcon, 
  CurrencyRupeeIcon,
  BuildingOfficeIcon 
} from '@heroicons/react/24/outline';
import { getCurrentFinancialYear } from '../../utils/financialYear';

const PfReport = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(getCurrentFinancialYear());
  const [reportType, setReportType] = useState('monthly');

  // Mock data - will be replaced with API call
  const pfData = {
    summary: {
      totalEmployees: 3,
      totalEmployeeContribution: 5400,
      totalEmployerContribution: 5400,
      totalPfDeduction: 10800,
      averageContributionPerEmployee: 1800
    },
    employees: [
      {
        employeeId: 'EMP001',
        employeeName: 'Dorothy Sanchez',
        department: 'Sales',
        basicSalary: 27200,
        pfEmployeeContribution: 1800,
        pfEmployerContribution: 1800,
        uan: '100123456789',
        pfNumber: 'PF001',
        status: 'Active'
      },
      {
        employeeId: 'EMP002',
        employeeName: 'Maria Martinez',
        department: 'Design',
        basicSalary: 27200,
        pfEmployeeContribution: 1800,
        pfEmployerContribution: 1800,
        uan: '100123456790',
        pfNumber: 'PF002',
        status: 'Active'
      },
      {
        employeeId: 'EMP003',
        employeeName: 'Jane Moore',
        department: 'Quality Assurance',
        basicSalary: 27200,
        pfEmployeeContribution: 1800,
        pfEmployerContribution: 1800,
        uan: '100123456791',
        pfNumber: 'PF003',
        status: 'Active'
      }
    ]
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handleDownloadECR = () => {
    // Generate ECR file format
    const ecrData = pfData.employees.map(emp => ({
      UAN: emp.uan,
      'Member Id': emp.pfNumber,
      'Member Name': emp.employeeName,
      'Gross Wages': emp.basicSalary,
      'EPF Wages': emp.basicSalary,
      'EPS Wages': emp.basicSalary,
      'EDLI Wages': emp.basicSalary,
      'EPF Contribution (Employee)': emp.pfEmployeeContribution,
      'EPS Contribution (Employee)': Math.min(emp.pfEmployeeContribution, 1250), // EPS ceiling
      'EPF Contribution (Employer)': emp.pfEmployerContribution,
      'EPS Contribution (Employer)': Math.min(emp.pfEmployerContribution, 1250),
      'EDLI Contribution (Employer)': Math.round(emp.basicSalary * 0.005), // 0.5% of basic
      'NCP Days': 0,
      'Refund of Advances': 0
    }));

    // Convert to CSV
    const headers = Object.keys(ecrData[0]);
    const csvContent = [
      headers.join(','),
      ...ecrData.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ECR_${selectedMonth}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    // Generate PF report CSV
    const reportData = pfData.employees.map(emp => ({
      'Employee ID': emp.employeeId,
      'Employee Name': emp.employeeName,
      'Department': emp.department,
      'UAN': emp.uan,
      'PF Number': emp.pfNumber,
      'Basic Salary': emp.basicSalary,
      'Employee Contribution': emp.pfEmployeeContribution,
      'Employer Contribution': emp.pfEmployerContribution,
      'Total Contribution': emp.pfEmployeeContribution + emp.pfEmployerContribution,
      'Status': emp.status
    }));

    const headers = Object.keys(reportData[0]);
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PF_Report_${selectedMonth}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="w-full lg:w-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-2">
            PF Report
          </h1>
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Employee & Employer Provident Fund contributions with ECR generation</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <button
            onClick={handleDownloadECR}
            className="flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base"
          >
            <DocumentArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Download ECR
          </button>
          <button
            onClick={handleDownloadReport}
            className="flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base"
          >
            <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Download Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total Employees</h3>
              <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {pfData.summary.totalEmployees}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl ml-3">
              <UserGroupIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Employee Contribution</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {formatCurrency(pfData.summary.totalEmployeeContribution)}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl ml-3">
              <CurrencyRupeeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Employer Contribution</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                {formatCurrency(pfData.summary.totalEmployerContribution)}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-purple-100 to-violet-100 rounded-2xl ml-3">
              <BuildingOfficeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Total PF Deduction</h3>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                {formatCurrency(pfData.summary.totalPfDeduction)}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl ml-3">
              <ShieldCheckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-xl border border-white/20">
        <h3 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-4 sm:mb-6">
          Report Filters
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
            >
              <option value="monthly">Monthly Report</option>
              <option value="annual">Annual Report</option>
              <option value="quarterly">Quarterly Report</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
              disabled={reportType === 'annual'}
            >
              {months.map((month, index) => (
                <option key={index} value={index + 1}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
            >
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">Financial Year</label>
            <select
              value={selectedFinancialYear}
              onChange={(e) => setSelectedFinancialYear(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-md text-sm sm:text-base"
              disabled={reportType === 'monthly'}
            >
              <option value="2025-26">2025-26</option>
              <option value="2024-25">2024-25</option>
              <option value="2023-24">2023-24</option>
            </select>
          </div>
        </div>
      </div>

      {/* Employee-wise PF Details */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-xl border border-white/20">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <h3 className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">
            Employee-wise PF Contributions
          </h3>
          <div className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-3 sm:px-4 py-2 rounded-full">
            {months[selectedMonth - 1]} {selectedYear}
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tl-xl">
                    Employee
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    UAN / PF Number
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Basic Salary
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Employee Contribution
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                    Employer Contribution
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Contribution
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tr-xl">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pfData.employees.map((employee) => (
                  <tr key={employee.employeeId} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 hover:shadow-md">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs sm:text-sm font-semibold text-gray-900">
                          {employee.employeeName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {employee.employeeId} • {employee.department}
                        </div>
                        <div className="text-xs text-gray-500 sm:hidden">
                          UAN: {employee.uan}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-xs sm:text-sm font-medium text-gray-900">{employee.uan}</div>
                      <div className="text-xs text-gray-500">{employee.pfNumber}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                      {formatCurrency(employee.basicSalary)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-green-600 hidden md:table-cell">
                      {formatCurrency(employee.pfEmployeeContribution)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-purple-600 hidden lg:table-cell">
                      {formatCurrency(employee.pfEmployerContribution)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-blue-600">
                      {formatCurrency(employee.pfEmployeeContribution + employee.pfEmployerContribution)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 sm:px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800">
                        {employee.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 font-semibold border-t-2 border-gray-300">
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap" colSpan="2">
                    <div className="text-sm sm:text-base font-bold text-gray-900">
                      Total ({pfData.employees.length} employees)
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-gray-900">
                    {formatCurrency(pfData.employees.reduce((sum, emp) => sum + emp.basicSalary, 0))}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-green-600 hidden md:table-cell">
                    {formatCurrency(pfData.summary.totalEmployeeContribution)}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-purple-600 hidden lg:table-cell">
                    {formatCurrency(pfData.summary.totalEmployerContribution)}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-bold text-blue-600">
                    {formatCurrency(pfData.summary.totalPfDeduction)}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 sm:px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800">
                      Summary
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ECR Information */}
      <div className="bg-gradient-to-r from-green-50/70 to-emerald-50/70 backdrop-blur-sm rounded-2xl p-4 sm:p-8 shadow-xl border border-green-100/50">
        <h3 className="text-lg sm:text-2xl font-semibold bg-gradient-to-r from-green-800 to-emerald-800 bg-clip-text text-transparent mb-4 sm:mb-6">
          ECR File Information
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">File Format</h4>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-2 sm:space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                CSV format compatible with EPFO portal
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Includes UAN, Member ID, wages, and contributions
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Auto-calculated EPS and EDLI contributions
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Ready for direct upload to government portal
              </li>
            </ul>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">Compliance Notes</h4>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-2 sm:space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                EPS contribution ceiling: ₹1,250 per month
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                EDLI contribution: 0.5% of basic salary
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                File must be uploaded by 15th of following month
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2 mr-2 sm:mr-3 flex-shrink-0"></span>
                Verify UAN numbers before submission
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PfReport;